import React, { useState } from 'react';
import { useHillTargets } from '../../hooks/useHillTargets';
import { useTheaterMode } from '../../contexts/TheaterModeContext';
import { useCameraDirector } from '../../contexts/CameraDirectorContext';
import { useReplay } from '../../contexts/ReplayContext';
import { useConversation } from '../../contexts/ConversationContext';
import { buildReplaySteps } from '../conversation/chatdag/utils/replaySteps';
import { HillTargetCombobox } from './HillTargetCombobox';

// TH-2 — persistent "what we're climbing toward" banner at the top of the trail.
// READS the resolved trail goal (never owns binding); soft prompt when none.
// TH-3a — also hosts the Theater Mode toggle (the objective bar stays visible in theater).
export function TrailObjectiveBanner({ sessionId }: { sessionId?: string }) {
  const { targets, setTarget } = useHillTargets(sessionId ?? null);
  const { theater, toggle, setTheater } = useTheaterMode();
  const { follow, paused, toggleFollow } = useCameraDirector();
  const replay = useReplay();
  const { state } = useConversation();
  const [editing, setEditing] = useState(false);
  const trail = targets.find((t) => t.scope_kind === 'trail' && (t.scope_ref ?? null) === null);
  const title = trail?.title ?? trail?.label ?? null;

  // TH-3c — ▶ Replay is a MODE (not a lever): replays the trail's DAG growth.
  const stepCount = React.useMemo(() => buildReplaySteps((state.messages ?? []) as any).length, [state.messages]);
  const canReplay = stepCount >= 2;
  const enterReplay = () => {
    if (replay.active) { replay.exit(); return; }       // plan-triad B1: exit() centralizes teardown (theater+ring) → bare call is correct
    const prior = theater;                              // plan-triad B1: capture BEFORE the flip — synchronous, at the source (NOT a late canvas effect)
    setTheater(true);                                   // rev2: auto-enter theater (stage)
    replay.enter(state.messages as any, buildReplaySteps(state.messages as any), prior);
  };
  const ReplayBtn = (
    <span className="flex items-center gap-1 pl-2 ml-1 border-l border-amber-300/50 dark:border-amber-700/50">
      <span className="text-[10px] uppercase tracking-wide text-amber-700/70 dark:text-amber-300/70">Replay</span>
      <button type="button" data-testid="replay-toggle" onClick={enterReplay} disabled={!canReplay}
              aria-label={replay.active ? 'Exit replay' : 'Replay the trail’s growth'}
              title={canReplay ? (replay.active ? 'Exit replay' : 'Replay the trail’s growth') : 'Replay — needs 2+ exchanges'}
              className={`text-sm leading-none px-1 ${replay.active ? 'text-amber-600 dark:text-amber-300'
                : canReplay ? 'text-amber-700/60 dark:text-amber-300/50 hover:text-amber-700 dark:hover:text-amber-200'
                : 'text-amber-700/25 dark:text-amber-300/20 cursor-not-allowed'}`}>
        ▶
      </button>
    </span>
  );

  const TheaterBtn = (
    <button type="button" data-testid="theater-toggle" onClick={toggle}
            aria-label={theater ? 'Exit theater mode (t)' : 'Enter theater mode (t)'}
            title={theater ? 'Exit theater mode (t)' : 'Enter theater mode (t)'}
            className="text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 text-sm leading-none px-1">
      {theater ? '⤡' : '⤢'}
    </button>
  );

  // TH-3b — the Camera Director follow toggle (⌖). Three legible states:
  // off / following / paused (the user panned away — following is suspended).
  const followCls = !follow
    ? 'text-amber-700/50 dark:text-amber-300/40 hover:text-amber-700 dark:hover:text-amber-200'
    : paused
      ? 'text-amber-600/40 dark:text-amber-300/40 italic'   // paused: muted while the user explores
      : 'text-amber-600 dark:text-amber-300';               // following: solid
  const followLabel = !follow ? 'Follow new turns (f)'
    : paused ? 'Following paused — panned away (f)' : 'Stop following new turns (f)';
  const FollowBtn = (
    <button type="button" data-testid="director-follow-toggle" onClick={toggleFollow}
            aria-label={followLabel} title={followLabel}
            className={`text-sm leading-none px-1 ${followCls}`}>
      ⌖
    </button>
  );

  if (editing && sessionId) {
    return (
      <div data-testid="trail-objective-banner"
           className="px-4 py-2 border-b border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20">
        <HillTargetCombobox
          current={title ? { title, source: 'trail' } : null}
          onPick={async (target) => { await setTarget({ scope_kind: 'trail', scope_ref: null, target }); setEditing(false); }}
        />
      </div>
    );
  }
  if (title) {
    return (
      <div data-testid="trail-objective-banner"
           className="flex items-center gap-2 px-4 py-2 border-b border-amber-200 dark:border-amber-800/60
                      bg-amber-50 dark:bg-amber-900/20">
        <span aria-hidden className="text-amber-600 dark:text-amber-400">⛰</span>
        <span className="text-[10px] uppercase tracking-wide text-amber-700/70 dark:text-amber-300/70">Trail objective</span>
        <span data-testid="trail-objective-title" className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{title}</span>
        <button type="button" data-testid="trail-objective-edit" onClick={() => setEditing(true)}
                className="ml-auto text-xs text-amber-700 dark:text-amber-300 hover:underline">change</button>
        {FollowBtn}
        {TheaterBtn}
        {ReplayBtn}
      </div>
    );
  }
  return (
    <div data-testid="trail-objective-banner"
         className="w-full flex items-center gap-2 px-4 py-2 border-b border-dashed
                    border-amber-200 dark:border-amber-800/50 bg-amber-50/40 dark:bg-amber-900/10
                    text-xs text-amber-700/80 dark:text-amber-300/70">
      <button type="button" onClick={() => setEditing(true)}
              className="flex items-center gap-2 text-left hover:underline">
        <span aria-hidden>⛰</span>
        <span data-testid="trail-objective-empty">no objective yet — set one</span>
      </button>
      <span className="ml-auto flex items-center">{FollowBtn}{TheaterBtn}{ReplayBtn}</span>
    </div>
  );
}
